import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { 
  LogOut, 
  Wallet as WalletIcon, 
  Bell, 
  User, 
  Settings,
  Home,
  Package,
  Users,
  UserCheck,
  ShoppingCart,
  TrendingUp,
  FileText,
  Tag,
  Palette,
  Receipt,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Menu,
  Shield,
  Activity,
  MessageSquare,
  UserCog,
  Gift,
  FolderOpen,
  Megaphone,
  Server,
  Store,
  Globe,
  XCircle,
  ClipboardList,
  RefreshCw as ArrowRightLeft,
  Settings2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Admin role types for RBAC
const ADMIN_ROLES = ['admin', 'level_3_support', 'level_2_support', 'level_1_support'];

// Helper function to get display name for roles
const getRoleDisplayName = (role) => {
  switch (role) {
    case 'reseller':
      return 'Synergy Partner';
    case 'sub_reseller':
      return 'Channel Partner';
    case 'admin':
      return 'Admin';
    case 'level_3_support':
      return 'Level 3 Support';
    case 'level_2_support':
      return 'Level 2 Support';
    case 'level_1_support':
      return 'Level 1 Support';
    case 'client':
      return 'Client';
    default:
      return role?.replace('_', ' ');
  }
};

export const DashboardLayout = ({ children, role }) => {
  const { user, logout, isSuspended } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [walletBalance, setWalletBalance] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [customLogo, setCustomLogo] = useState(null);
  const [rbacModules, setRbacModules] = useState([]);
  const [rbacPermissions, setRbacPermissions] = useState([]);
  const [masqueradeMode, setMasqueradeMode] = useState(false);
  const [masqueradeClientName, setMasqueradeClientName] = useState('');
  const [accessedByInfo, setAccessedByInfo] = useState(null); // banner for accessed user
  const [expandedNavGroups, setExpandedNavGroups] = useState([]);
  const [pendingDomainCount, setPendingDomainCount] = useState(0);

  // Default CloudSetu logo path
  const defaultLogo = '/cloudsetu-logo.png';
  
  // Determine if this is an admin-type dashboard (includes all support roles)
  const isAdminDashboard = ADMIN_ROLES.includes(role) || ADMIN_ROLES.includes(user?.role);
  const effectiveRole = user?.role || role;

  // Auto-expand nav groups based on current path
  useEffect(() => {
    const navItems = getNavItems();
    navItems.forEach(item => {
      if (item.children?.some(c => c.path === location.pathname)) {
        setExpandedNavGroups(prev => prev.includes(item.label) ? prev : [...prev, item.label]);
      }
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Check for Admin Support PIN masquerade session
    const isMasquerade = localStorage.getItem('masquerade_mode') === 'true';
    if (isMasquerade) {
      setMasqueradeMode(true);
      setMasqueradeClientName(localStorage.getItem('masquerade_client_name') || 'Client');
    }

    // Fetch RBAC permissions for admin-type roles
    if (ADMIN_ROLES.includes(user?.role)) {
      fetchRbacPermissions();
    }
    
    if (!isAdminDashboard) {
      if (effectiveRole !== 'sub_reseller') fetchWalletBalance();
      fetchWhiteLabelSettings();
      fetchPartnerNotifications();  // Notifications for SP/CP/Client
    }
    if (isAdminDashboard) {
      fetchNotifications();
    }
    // Fetch pending domain request count for roles that can approve
    if (['reseller', 'sub_reseller', 'admin'].includes(effectiveRole)) {
      fetchPendingDomainCount();
    }
  }, [role, user?.role, isAdminDashboard]);

  const fetchRbacPermissions = async () => {
    try {
      const response = await axios.get(`${API}/rbac/my-permissions`);
      setRbacModules(response.data.modules || []);
      setRbacPermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Failed to fetch RBAC permissions');
    }
  };

  const fetchWhiteLabelSettings = async () => {
    try {
      const response = await axios.get(`${API}/white-label/settings`);
      if (response.data?.logo_url) {
        // If logo URL is a relative path, prepend backend URL
        const logoUrl = response.data.logo_url.startsWith('/') 
          ? `${BACKEND_URL}${response.data.logo_url}`
          : response.data.logo_url;
        setCustomLogo(logoUrl);
      }
    } catch (error) {
      // Settings might not exist for some users, that's okay
      console.log('No custom logo found, using default');
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`);
      setWalletBalance(response.data.balance);
    } catch (error) {
      console.error('Failed to fetch wallet balance');
    }
  };

  const fetchPendingDomainCount = async () => {
    try {
      const response = await axios.get(`${API}/domain/requests/pending-count`);
      setPendingDomainCount(response.data?.count || 0);
    } catch {
      // non-critical
    }
  };

  // Listen for wallet credit events from child components (coupon, topup, etc.)
  useEffect(() => {
    if (isAdminDashboard || effectiveRole === 'sub_reseller') return;
    const handler = () => fetchWalletBalance();
    window.addEventListener('cloudsetu:wallet-updated', handler);
    return () => window.removeEventListener('cloudsetu:wallet-updated', handler);
  }, [isAdminDashboard, effectiveRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/admin/notifications`);
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const fetchPartnerNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      const notifs = response.data?.notifications || response.data || [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.is_read).length);
      // Check for dashboard_access_started notification (within last 2 hours)
      const twoHoursAgo = Date.now() - 2 * 60 * 60 * 1000;
      const accessNotif = notifs.find(n =>
        n.notification_type === 'dashboard_access_started' &&
        !n.is_read &&
        new Date(n.created_at).getTime() > twoHoursAgo
      );
      setAccessedByInfo(accessNotif?.metadata || null);
    } catch (error) {
      // Silently fail - notifications are non-critical
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const endpoint = isAdminDashboard
        ? `${API}/admin/notifications/${notificationId}/read`
        : `${API}/notifications/${notificationId}/read`;
      await axios.put(endpoint);
      if (isAdminDashboard) fetchNotifications();
      else fetchPartnerNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      const endpoint = isAdminDashboard
        ? `${API}/admin/notifications/mark-all-read`
        : `${API}/notifications/mark-all-read`;
      await axios.put(endpoint);
      if (isAdminDashboard) fetchNotifications();
      else fetchPartnerNotifications();
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const handleLogout = () => {
    logout();
    const role = user?.role;
    if (['admin', 'level_1_support', 'level_2_support', 'level_3_support'].includes(role)) {
      navigate('/admin/login');
    } else if (['reseller', 'sub_reseller'].includes(role)) {
      navigate('/partner/login');
    } else {
      navigate('/client/login');
    }
  };

  const handleExitClientView = () => {
    localStorage.removeItem('masquerade_mode');
    localStorage.removeItem('masquerade_client_name');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.close(); // Close masquerade tab; admin's original tab remains intact
  };

  const getNavItems = () => {
    // For admin-type roles, use RBAC-based filtering
    if (isAdminDashboard) {
      const allAdminItems = [
        { label: 'Dashboard', path: '/admin/dashboard', icon: Home, module: 'dashboard' },
        { label: 'Products', path: '/admin/products', icon: Package, module: 'products' },
        { label: 'Partners', path: '/admin/resellers', icon: Users, module: 'partners' },
        { label: 'Clients', path: '/admin/clients', icon: UserCheck, module: 'clients' },
        { label: 'Quotations', path: '/admin/quotations', icon: FileText, module: 'quotations' },
        { label: 'Orders', path: '/admin/orders', icon: ShoppingCart, module: 'orders' },
        { label: 'SaaS Orders', path: '/admin/saas-orders', icon: Package, module: 'orders' },
        { label: 'Invoices', path: '/admin/invoices', icon: Receipt, module: 'invoices' },
        { label: 'Wallets', path: '/admin/wallets', icon: WalletIcon, module: 'wallets' },
        { label: 'Transactions', path: '/admin/transactions', icon: Receipt, module: 'transactions' },
        { label: 'Offers & Loyalty', icon: Megaphone, module: 'offers', children: [
          { label: 'Offers', path: '/admin/offers', icon: Megaphone, module: 'offers' },
          { label: 'Coupons', path: '/admin/coupons', icon: Tag, module: 'coupons' }
        ]},
        { label: 'Documents', path: '/admin/documents', icon: FileText, module: 'documents' },
        { label: 'Support', path: '/admin/support', icon: MessageSquare, module: 'support' },
        { label: 'Activity Logs', path: '/admin/activity-logs', icon: Shield, module: 'activity_logs' },
        { label: 'Notifications', path: '/admin/notifications', icon: Bell, module: 'notifications' },
        { label: 'Revenue', path: '/admin/revenue', icon: TrendingUp, module: 'revenue' },
        { label: 'Users', path: '/admin/users', icon: UserCog, module: 'users' },
        { label: 'Domain Manager', path: '/admin/domains', icon: Globe, module: 'domain_manager', badge: pendingDomainCount > 0 ? pendingDomainCount : 0 },
        { label: 'WhatsApp', path: '/admin/whatsapp-settings', icon: MessageSquare },
        { label: 'White Label', path: '/admin/white-label', icon: Palette },
      ];
      
      // If RBAC modules are loaded, filter by them
      // Items without a module property are always shown (non-RBAC items like WhatsApp settings)
      if (rbacModules.length > 0) {
        return allAdminItems
          .filter(item => {
            if (!item.module) return true;  // no module = always visible
            if (item.children) {
              return rbacModules.includes(item.module) || item.children.some(c => rbacModules.includes(c.module));
            }
            return rbacModules.includes(item.module);
          })
          .map(item => ({
            ...item,
            children: item.children
              ? item.children.filter(c => !c.module || rbacModules.includes(c.module))
              : undefined,
          }));
      }
      
      // Default to showing all items for admin role if RBAC not yet loaded
      if (effectiveRole === 'admin') {
        return allAdminItems;
      }
      
      return allAdminItems;
    } else if (role === 'reseller' || role === 'sub_reseller') {
      const isChannelPartner = role === 'sub_reseller';
      const resellerItems = [
        { label: 'Dashboard', path: '/reseller/dashboard', icon: Home },
        { label: 'Quotations', path: '/reseller/quotations', icon: FileText },
        { label: 'Orders', path: '/reseller/orders', icon: ShoppingCart },
        { label: 'My Services', path: '/reseller/my-services', icon: Server },
        { label: 'Pricing', path: '/reseller/pricing', icon: Tag },
        { label: 'Catalog', path: '/reseller/catalog', icon: Package },
        { label: 'Domains', path: '/reseller/domains', icon: Globe, badge: pendingDomainCount > 0 ? pendingDomainCount : 0 },
        { label: 'Clients', path: '/reseller/clients', icon: Users },
        // Wallet and Transactions hidden for Channel Partners
        ...(!isChannelPartner ? [
          { label: 'Wallet', path: '/reseller/wallet', icon: WalletIcon },
          { label: 'Transactions', path: '/reseller/transactions', icon: Receipt },
        ] : []),
        { label: 'Invoices', path: '/reseller/invoices', icon: FileText },
        { label: 'Support', path: '/reseller/support', icon: MessageSquare },
        { label: 'White Label', path: '/reseller/white-label', icon: Palette },
        { label: 'Profile', path: '/reseller/profile', icon: User },
      ];
      // For suspended users, only show Support and Invoices
      if (isSuspended) {
        return resellerItems.filter(item => 
          item.path === '/reseller/support' || item.path === '/reseller/invoices'
        );
      }
      return resellerItems;
    } else if (role === 'client') {
      const isAdminDirect = user?.created_by_admin_direct !== false;
      const clientItems = [
        { label: 'Dashboard',         path: '/client/dashboard',       icon: Home },
        { label: 'Services',          path: '/client/services',         icon: Package },
        { label: 'Buy Domain',        path: '/client/buy-domain',       icon: Globe },
        { label: 'Our Products',      path: '/client/our-products',     icon: Store },
        { label: 'Quotes & Orders',   path: '/client/quotes-orders',    icon: FileText },
        { label: 'Billing & Payments', path: '/client/billing',         icon: Receipt },
        ...(isAdminDirect ? [{ label: 'Referral Program', path: '/client/referral-program', icon: Gift }] : []),
        { label: 'Support',           path: '/client/support',          icon: MessageSquare },
      ];
      // For suspended clients, only show Support and Billing
      if (isSuspended) {
        return clientItems.filter(item => 
          item.path === '/client/support' || item.path === '/client/billing'
        );
      }
      return clientItems;
    }
    return [];
  };

  const navItems = getNavItems();

  const NavItem = ({ item, collapsed }) => {
    // Match full URL (path + query) for query-param-based tabs, otherwise match pathname only
    const fullUrl = location.pathname + location.search;
    const isActive = item.path
      ? item.path.includes('?')
        ? fullUrl === item.path || fullUrl.startsWith(item.path)
        : location.pathname === item.path
      : false;
    const isChildActive = item.children?.some(c => location.pathname === c.path);
    const isGroupExpanded = expandedNavGroups.includes(item.label) || isActive || isChildActive;
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      return (
        <div>
          <button
            onClick={() => {
              if (collapsed) { navigate(item.path); setMobileMenuOpen(false); return; }
              setExpandedNavGroups(prev =>
                prev.includes(item.label) ? prev.filter(g => g !== item.label) : [...prev, item.label]
              );
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group
              ${(isActive || isChildActive)
                ? 'bg-blue-50 text-blue-600 font-medium'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }
              ${collapsed ? 'justify-center px-3' : ''}
            `}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <Icon className={`h-5 w-5 flex-shrink-0 ${(isActive || isChildActive) ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
            {!collapsed && (
              <>
                <span className="text-sm flex-1 text-left">{item.label}</span>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isGroupExpanded ? 'rotate-180' : ''}`} />
              </>
            )}
          </button>
          {!collapsed && isGroupExpanded && (
            <div className="ml-3 mt-1 mb-1 space-y-0.5 border-l-2 border-blue-100 pl-3">
              {item.children.map(child => {
                const isChildPathActive = location.pathname === child.path;
                const ChildIcon = child.icon;
                return (
                  <button
                    key={child.path}
                    onClick={() => { navigate(child.path); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all
                      ${isChildPathActive
                        ? 'bg-blue-50 text-blue-600 font-medium'
                        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                      }`}
                    data-testid={`nav-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <ChildIcon className={`h-4 w-4 flex-shrink-0 ${isChildPathActive ? 'text-blue-500' : 'text-slate-400'}`} />
                    <span>{child.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <button
        onClick={() => {
          navigate(item.path);
          setMobileMenuOpen(false);
        }}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group
          ${isActive 
            ? 'bg-blue-50 text-blue-600 font-medium' 
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          }
          ${collapsed ? 'justify-center px-3' : ''}
        `}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className="relative flex-shrink-0">
          <Icon className={`h-5 w-5 ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
          {item.badge > 0 && (
            <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5 leading-none">
              {item.badge > 9 ? '9+' : item.badge}
            </span>
          )}
        </div>
        {!collapsed && (
          <span className="text-sm flex-1 text-left">{item.label}</span>
        )}
        {!collapsed && item.badge > 0 && (
          <span className="bg-red-100 text-red-600 text-[10px] font-semibold rounded-full px-1.5 py-0.5 leading-none">
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Accessor's Banner: shown when this tab is a masquerade/support-PIN session (SP/CP only — Admin never needs it) */}
      {masqueradeMode && !isAdminDashboard && (
        <div
          className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white px-4 flex items-center justify-between shadow-lg"
          style={{ height: '44px' }}
          data-testid="masquerade-banner"
        >
          <div className="flex items-center gap-2.5">
            <Shield className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm font-medium">
              You are viewing <strong>{masqueradeClientName}</strong>'s dashboard
            </span>
          </div>
          <Button
            onClick={handleExitClientView}
            size="sm"
            variant="outline"
            className="h-7 text-xs border-white/50 text-white bg-transparent hover:bg-white/20 px-3"
            data-testid="exit-client-view-btn"
          >
            <LogOut className="h-3 w-3 mr-1.5" />
            Exit View
          </Button>
        </div>
      )}
      {/* Accessed-user Banner: shown to the account being viewed by a support accessor */}
      {!masqueradeMode && accessedByInfo && (
        <div
          className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white px-4 flex items-center justify-between shadow-lg"
          style={{ height: '44px' }}
          data-testid="accessed-by-banner"
        >
          <div className="flex items-center gap-2.5">
            <Shield className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm font-medium">
              Your dashboard is currently being accessed by{' '}
              <strong>{accessedByInfo.accessor_name || accessedByInfo.accessor_email}</strong>
              {accessedByInfo.accessor_role && ` (${accessedByInfo.accessor_role.replace(/_/g, ' ')})`}
            </span>
          </div>
          <button
            onClick={() => setAccessedByInfo(null)}
            className="text-white/80 hover:text-white text-xs underline"
          >
            Dismiss
          </button>
        </div>
      )}
      <div className={`min-h-screen bg-slate-50 flex flex-col w-full max-w-full ${((masqueradeMode && !isAdminDashboard) || accessedByInfo) ? 'pt-11' : ''}`}>
      {/* Sidebar - Desktop */}
      <aside 
        className={`hidden lg:flex flex-col bg-white border-r border-slate-200 transition-all duration-300 fixed z-40
          ${sidebarCollapsed ? 'w-20' : 'w-64'}
        `}
        style={{
          top: (masqueradeMode && !isAdminDashboard) ? '44px' : '0',
          height: (masqueradeMode && !isAdminDashboard) ? 'calc(100vh - 44px)' : '100vh',
        }}
      >
        {/* Logo - Always CloudSetu */}
        <div className={`h-20 flex items-center justify-center border-b border-slate-100 px-4`}>
          <img 
            src={defaultLogo} 
            alt="CloudSetu" 
            className={`object-contain ${sidebarCollapsed ? 'h-12 w-12' : 'h-14 max-w-[180px]'}`}
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavItem key={item.path || item.label} item={item} collapsed={sidebarCollapsed} />
          ))}
        </nav>

        {/* User Info at Bottom */}
        <div className={`p-4 border-t border-slate-100 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
          {!sidebarCollapsed ? (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                <User className="h-5 w-5 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{user?.email}</p>
                <p className="text-xs text-slate-400" data-testid="sidebar-role-label">{getRoleDisplayName(effectiveRole)}</p>
              </div>
            </div>
          ) : (
            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
              <User className="h-5 w-5 text-slate-500" />
            </div>
          )}
        </div>

        {/* Collapse Button */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-slate-200 rounded-full shadow-sm flex items-center justify-center hover:bg-slate-50 transition-colors"
          data-testid="sidebar-toggle"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronLeft className="h-4 w-4 text-slate-400" />
          )}
        </button>
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Mobile */}
      <aside 
        className={`lg:hidden fixed left-0 w-64 bg-white z-50 transform transition-transform duration-300
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{
          top: (masqueradeMode && !isAdminDashboard) ? '44px' : '0',
          height: (masqueradeMode && !isAdminDashboard) ? 'calc(100vh - 44px)' : '100vh',
        }}
      >
        {/* Logo - Always CloudSetu */}
        <div className="h-20 flex items-center justify-center border-b border-slate-100 px-4">
          <img 
            src={defaultLogo} 
            alt="CloudSetu" 
            className="h-14 max-w-[180px] object-contain"
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavItem key={item.path || item.label} item={item} collapsed={false} />
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
              <User className="h-5 w-5 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-700 truncate">{user?.email}</p>
              <p className="text-xs text-slate-400">{getRoleDisplayName(effectiveRole)}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Top Header Bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5 text-slate-600" />
          </button>

          {/* Spacer for desktop */}
          <div className="hidden lg:block" />

          {/* Right Side Actions */}
          <div className="flex items-center gap-4">
            {/* Partner Logo Display in header - Only show if partner has uploaded a logo */}
            {role !== 'admin' && customLogo && (
              <div 
                className="flex items-center justify-center h-14 px-4 rounded-2xl bg-white border border-slate-200 shadow-sm"
                data-testid="header-logo"
              >
                <img 
                  src={customLogo} 
                  alt="Partner Logo" 
                  className="h-10 max-w-[120px] object-contain"
                />
              </div>
            )}

            {/* Wallet Display for non-admin, non-Channel-Partner users who have wallet access */}
            {role !== 'admin' && effectiveRole !== 'sub_reseller' && user?.created_by_admin_direct !== false && (
              <div 
                className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors" 
                data-testid="wallet-display"
                onClick={() => navigate(role === 'client' ? '/client/dashboard' : '/reseller/wallet')}
              >
                <WalletIcon className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-700">
                  {walletBalance !== null ? `₹${walletBalance.toLocaleString()}` : '...'}
                </span>
              </div>
            )}

            {/* Notification Bell — shown for ALL roles */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="relative h-10 w-10 rounded-lg" data-testid="notification-bell">
                  <Bell className="h-5 w-5 text-slate-500" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="notification-count">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <span className="font-semibold">Notifications</span>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs h-7">
                      Mark all read
                    </Button>
                  )}
                </div>

                {/* Domain requests pending summary banner */}
                {pendingDomainCount > 0 && (
                  <div
                    className="flex items-center gap-2.5 px-3 py-2.5 bg-amber-50 border-b border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors"
                    onClick={() => {
                      const path = ['reseller', 'sub_reseller'].includes(effectiveRole)
                        ? '/reseller/domains'
                        : '/admin/domains';
                      navigate(path);
                    }}
                    data-testid="pending-domain-banner"
                  >
                    <Globe className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <p className="text-xs font-medium text-amber-800 flex-1">
                      {pendingDomainCount} domain request{pendingDomainCount > 1 ? 's' : ''} pending your approval
                    </p>
                    <span className="bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {pendingDomainCount > 9 ? '9+' : pendingDomainCount}
                    </span>
                  </div>
                )}

                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-3 py-4 text-center text-slate-500 text-sm">
                      No notifications
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((notification) => (
                      <DropdownMenuItem 
                        key={notification.id} 
                        className={`px-3 py-3 cursor-pointer ${!notification.is_read ? 'bg-blue-50' : ''}`}
                        onClick={() => markAsRead(notification.id)}
                        data-testid={`notification-${notification.id}`}
                      >
                        <div className="space-y-1">
                          <p className={`text-sm ${!notification.is_read ? 'font-medium' : ''}`}>
                            {notification.message}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-10 w-10 rounded-lg p-0" data-testid="profile-dropdown">
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-slate-500" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b">
                  <p className="text-sm font-medium text-slate-700">{user?.email}</p>
                  <p className="text-xs text-slate-400">{getRoleDisplayName(effectiveRole)}</p>
                </div>
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-link">
                  <Settings className="h-4 w-4 mr-2" />
                  Edit Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/security')} data-testid="security-link">
                  <Shield className="h-4 w-4 mr-2" />
                  Login Security
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-dropdown-item">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Content - Responsive padding */}
        <main className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overflow-hidden">
          {children}
        </main>
      </div>
    </div>
    </>
  );
};
